<template>
    <nav class="category-tabs" role="navigation" :aria-label="ariaLabel">
        <button
            v-if="showLeft"
            class="scroll-arrow left"
            aria-hidden="true"
            tabindex="-1"
            @click="scrollLeft()"
        >
            <span class="icon-arrow-previous" />
        </button>
        <ul ref="scrollContainer" class="nav" :aria-label="ariaLabel">
            <li
                v-for="(tab, index) in tabs"
                :key="index"
                :class="tab.id === activeTabId ? 'nav-item active' : 'nav-item'"
            >
                <a
                    v-if="tab.href"
                    :href="tab.href"
                    class="nav-link"
                    :aria-current="tab.id === activeTabId"
                    @click="clickHandler(tab.id)"
                >
                    {{ tab.label }}
                </a>
                <button
                    v-else
                    :aria-current="tab.id === activeTabId"
                    class="nav-link"
                    @click="clickHandler(tab.id)"
                >
                    <span v-if="tab.iconName" :class="'item-icon ' + tab.iconName" aria-hidden="true" />
                    {{ tab.label }}
                </button>
            </li>
        </ul>
        <button
            v-if="showRight"
            class="scroll-arrow right"
            aria-hidden="true"
            tabindex="-1"
            @click="scrollRight()"
        >
            <span class="icon-arrow-next" />
        </button>
    </nav>
</template>

<script lang="ts">
import type {PropType} from 'vue'
import {defineComponent} from 'vue';
import {CategoryTab} from "./models/CategoryTab";

export default defineComponent({
    name: 'CategoryTabs',
    props: {
        tabs: {
            type: Array as PropType<Array<CategoryTab>>,
            required: true
        },
        activeTabId: {
            type: String,
            required: true
        },
        ariaLabel: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            showLeft: false,
            showRight: false
        }
    },
    computed: {
    },
    mounted() {
        this.updateArrows();
        this.$refs.scrollContainer.addEventListener('scroll', this.updateArrows);
        window.addEventListener('resize', this.updateArrows);
    },
    beforeUnmount() {
        this.$refs.scrollContainer.removeEventListener('scroll', this.updateArrows);
        window.removeEventListener('resize', this.updateArrows);
    },
    methods: {
        clickHandler(tabId: string) {
            this.$emit('tab-click', tabId);
        },
        updateArrows() {
            const container = this.$refs.scrollContainer as HTMLElement;
            if (container) {
                this.showLeft = container.scrollLeft > 0;
                this.showRight = container.scrollLeft + container.clientWidth + 1 < container.scrollWidth;// On ajoute 1 pour se prémunir du delta causé par les arrondis
            }
        },
        scrollLeft() {
            const container = this.$refs.scrollContainer as HTMLElement;
            if (container) {
                container.scrollBy({left: -150, behavior: 'smooth'});
            }
        },
        scrollRight() {
            const container = this.$refs.scrollContainer as HTMLElement;
            if (container) {
                container.scrollBy({left: 150, behavior: 'smooth'});
            }
        }
    }
});
</script>
<style scoped lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

.category-tabs {
    position: relative;
}

.category-tabs .nav {
    padding: 5px 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 20px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        gap: 33px;
        justify-content: space-between;
    }
}

.category-tabs .nav::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
}

.category-tabs .nav-item .nav-link {
    display: block;
    padding-top: px-to-rem(10);
    padding-bottom: px-to-rem(10);
    padding-left: 0;
    padding-right: 0;
    width:120px;
    background-color: white;
    position: relative;
    font-size: px-to-rem(12);

    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        width:160px;
        font-size: px-to-rem(14);
    }
}

.category-tabs .nav-item .nav-link .item-icon {
    display: block;
    margin-bottom: px-to-rem(5);
    font-size: px-to-rem(40);
    pointer-events: none;
}


.category-tabs .nav-item .nav-link:before {
    position: absolute;
    bottom: -1px;
    display: block;
    width: 0;
    height: unset;
    content: "";
    border-bottom: .3125rem solid #f15e00;
    mask: none;
}

.category-tabs .nav-item.active .nav-link:before {
    width: 100%;
    transition: width .4s cubic-bezier(.33, 1, .68, 1);
}

.scroll-arrow {
    position: absolute;
    top: 0;
    height:100%;
    z-index: 10;
    cursor: pointer;
    padding: 0;
    border: none;
    background: transparent;
    width:60px;
    display: none;
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        display: block;
    }

}
.scroll-arrow.left {
    left: 0;
    background: linear-gradient(
            to right,
            rgba(255, 255, 255, 1) 80%,
            rgba(255, 255, 255, 0.8) 90%,
            rgba(255, 255, 255, 0)
    );
}
.scroll-arrow.right {
    right: 0;
    background: linear-gradient(
            to left,
            rgba(255, 255, 255, 1) 80%,
            rgba(255, 255, 255, 0.8) 90%,
            rgba(255, 255, 255, 0)
    );
}

</style>
